<template>
  <div
    v-show="!isHomePage"
    class="floating-home-button"
    :style="buttonStyle"
    @mousedown="handleMouseDown"
    @touchstart="handleTouchStart"
    @click="handleClick"
  >
    <el-icon class="home-icon"><HomeFilled /></el-icon>
  </div>
</template>

<script>
import { HomeFilled } from '@element-plus/icons-vue';
export default {
  components: {
    HomeFilled
  },
  name: 'FloatingHomeButton',
  data() {
    return {
      position: {
        x: 0,
        y: 0
      },
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      buttonSize: 60,
      animationFrame: null,
      resizeTimer: null,
      isHomePage: false,
      touchStartTime: 0,
      startPosition: { x: 0, y: 0 },
      hasMoved: false
    }
  },
  computed: {
    buttonStyle() {
      return {
        left: `${this.position.x}px`,
        top: `${this.position.y}px`,
        width: `${this.buttonSize}px`,
        height: `${this.buttonSize}px`
      }
    }
  },
  watch: {
    '$route'(to) {
      this.checkHomePage(to)
    }
  },
  mounted() {
    this.initPosition()
    this.checkHomePage(this.$route)
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleClick() {
      console.log('Vue click 事件触发', {
        isDragging: this.isDragging,
        hasMoved: this.hasMoved
      })
      
      // 如果是拖动操作或者有移动，不触发首页跳转
      if (this.isDragging || this.hasMoved) {
        console.log('阻止点击：拖动操作')
        this.hasMoved = false
        return
      }
      
      console.log('执行返回首页')
      this.navigateToHome()
    },
    
    handleMouseDown(e) {
      console.log('鼠标按下')
      this.startInteraction(e)
    },
    
    handleTouchStart(e) {
      console.log('触摸开始')
      this.startInteraction(e)
    },
    
    startInteraction(e) {
      e.preventDefault()
      e.stopPropagation()
      
      this.isDragging = false
      this.hasMoved = false
      this.touchStartTime = Date.now()
      
      const clientX = e.clientX || (e.touches && e.touches[0].clientX)
      const clientY = e.clientY || (e.touches && e.touches[0].clientY)
      
      if (clientX && clientY) {
        this.startPosition = { x: clientX, y: clientY }
        
        this.dragStart = {
          x: clientX - this.position.x,
          y: clientY - this.position.y
        }

        this.bindDragEvents()
      }
    },
    
    bindDragEvents() {
      document.addEventListener('mousemove', this.handleDragMove)
      document.addEventListener('touchmove', this.handleDragMove, { passive: false })
      document.addEventListener('mouseup', this.handleDragEnd)
      document.addEventListener('touchend', this.handleDragEnd)
      document.addEventListener('touchcancel', this.handleDragEnd)
    },
    
    handleDragMove(e) {
      const clientX = e.clientX || (e.touches && e.touches[0].clientX)
      const clientY = e.clientY || (e.touches && e.touches[0].clientY)
      
      if (clientX && clientY) {
        // 检查移动距离
        const moveDistance = Math.sqrt(
          Math.pow(clientX - this.startPosition.x, 2) + 
          Math.pow(clientY - this.startPosition.y, 2)
        )
        
        // 如果移动距离超过阈值，认为是拖动
        if (moveDistance > 5 && !this.isDragging) {
          this.isDragging = true
          this.hasMoved = true
          console.log('开始拖动，移动距离:', moveDistance)
        }
        
        if (this.isDragging) {
          e.preventDefault()
          e.stopPropagation()
          
          if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame)
          }
          
          this.animationFrame = requestAnimationFrame(() => {
            let newX = clientX - this.dragStart.x
            let newY = clientY - this.dragStart.y
            
            const maxX = window.innerWidth - this.buttonSize
            const maxY = window.innerHeight - this.buttonSize
            
            this.position.x = Math.max(0, Math.min(newX, maxX))
            this.position.y = Math.max(0, Math.min(newY, maxY))
          })
        }
      }
    },
    
    handleDragEnd() {
      console.log('交互结束', { 
        isDragging: this.isDragging,
        hasMoved: this.hasMoved,
        duration: Date.now() - this.touchStartTime
      })
      
      if (this.isDragging) {
        // 如果是拖动操作，处理吸附逻辑
        this.autoSnapToEdge()
        // 移除了 savePosition() 调用
      } else {
        // 如果不是拖动，可能是点击，但让 click 事件处理
        console.log('可能是点击操作')
        this.$router.push('/')
      }
      
      // 重置状态
      this.isDragging = false
      
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame)
        this.animationFrame = null
      }
      
      this.cleanupDragEvents()
    },
    
    cleanupDragEvents() {
      document.removeEventListener('mousemove', this.handleDragMove)
      document.removeEventListener('touchmove', this.handleDragMove)
      document.removeEventListener('mouseup', this.handleDragEnd)
      document.removeEventListener('touchend', this.handleDragEnd)
      document.removeEventListener('touchcancel', this.handleDragEnd)
    },
    
    navigateToHome() {
      console.log('跳转到首页')
      if (this.$route.path !== '/') {
        this.$router.push('/')
      } else {
        console.log('已经在首页')
      }
    },

    checkHomePage(route) {
      this.isHomePage = route.path === '/' || 
                       route.name === 'Home' || 
                       route.path === '/home' ||
                       route.path === '/login' ||
                       route.path === '/RoomHandbook/signature' ||
                       (route.meta && route.meta.isHomePage)
    },

    initPosition() {
      // 直接设置默认位置，不再从 localStorage 加载
      this.setDefaultPosition()
    },

    setDefaultPosition() {
      this.position = {
        x: window.innerWidth - this.buttonSize - 20,
        y: window.innerHeight - this.buttonSize - 60
      }
    },

    validatePosition(pos) {
      const maxX = Math.max(0, window.innerWidth - this.buttonSize)
      const maxY = Math.max(0, window.innerHeight - this.buttonSize)
      
      return {
        x: Math.max(0, Math.min(pos.x, maxX)),
        y: Math.max(0, Math.min(pos.y, maxY))
      }
    },

    autoSnapToEdge() {
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight
      const snapThreshold = 20
      
      const centerX = this.position.x + this.buttonSize / 2
      const centerY = this.position.y + this.buttonSize / 2
      
      const toLeft = centerX
      const toRight = windowWidth - centerX
      const toTop = centerY
      const toBottom = windowHeight - centerY
      
      const distances = {
        left: toLeft,
        right: toRight,
        top: toTop,
        bottom: toBottom
      }
      
      const minEdge = Object.keys(distances).reduce((min, edge) => 
        distances[edge] < distances[min] ? edge : min
      )
      
      if (distances[minEdge] <= snapThreshold) {
        switch (minEdge) {
          case 'left':
            this.position.x = 10
            break
          case 'right':
            this.position.x = windowWidth - this.buttonSize - 10
            break
          case 'top':
            this.position.y = 10
            break
          case 'bottom':
            this.position.y = windowHeight - this.buttonSize - 10
            break
        }
      }
      
      this.snapToCorner()
    },

    snapToCorner() {
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight
      const cornerThreshold = 20
      
      const centerX = this.position.x + this.buttonSize / 2
      const centerY = this.position.y + this.buttonSize / 2
      
      const toTopLeft = Math.sqrt(centerX * centerX + centerY * centerY)
      const toTopRight = Math.sqrt((windowWidth - centerX) * (windowWidth - centerX) + centerY * centerY)
      const toBottomLeft = Math.sqrt(centerX * centerX + (windowHeight - centerY) * (windowHeight - centerY))
      const toBottomRight = Math.sqrt((windowWidth - centerX) * (windowWidth - centerX) + (windowHeight - centerY) * (windowHeight - centerY))
      
      const cornerDistances = {
        topLeft: toTopLeft,
        topRight: toTopRight,
        bottomLeft: toBottomLeft,
        bottomRight: toBottomRight
      }
      
      const minCorner = Object.keys(cornerDistances).reduce((min, corner) => 
        cornerDistances[corner] < cornerDistances[min] ? corner : min
      )
      
      if (cornerDistances[minCorner] <= cornerThreshold) {
        switch (minCorner) {
          case 'topLeft':
            this.position.x = 10
            this.position.y = 10
            break
          case 'topRight':
            this.position.x = windowWidth - this.buttonSize - 10
            this.position.y = 10
            break
          case 'bottomLeft':
            this.position.x = 10
            this.position.y = windowHeight - this.buttonSize - 10
            break
          case 'bottomRight':
            this.position.x = windowWidth - this.buttonSize - 10
            this.position.y = windowHeight - this.buttonSize - 10
            break
        }
      }
    },

    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
      this.resizeTimer = setTimeout(() => {
        this.position = this.validatePosition(this.position)
        // 移除了 savePosition() 调用
      }, 150)
    }

    // 移除了 savePosition() 方法
  }
}
</script>

<style scoped>
.floating-home-button {
  position: fixed;
  z-index: 9999;
  background: rgba(128, 128, 128, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  pointer-events: auto !important;
  /* 移动端优化 */
  -webkit-tap-highlight-color: transparent;
  touch-action: pan-x pan-y;
}

.floating-home-button:hover {
  background: rgba(128, 128, 128, 0.6);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transform: scale(1.05);
}

.floating-home-button:active {
  transform: scale(0.95);
}

.home-icon {
  font-size: 24px;
  filter: grayscale(100%) brightness(150%);
  pointer-events: none;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .floating-home-button {
    width: 54px !important;
    height: 54px !important;
  }
  
  .home-icon {
    font-size: 22px;
  }
}

/* 小屏幕手机 */
@media (max-width: 480px) {
  .floating-home-button {
    width: 50px !important;
    height: 50px !important;
  }
  
  .home-icon {
    font-size: 20px;
  }
}
</style>